import '@/plugins/data-dog';
import '@/plugins/uppy';
import '@/shared/services/google_dev_mock';
import '@/styles/main.scss';
import { createApp } from 'vue';

import Api from '@/shared/services/bright_finder';
import assets from '@/shared/services/assets';
import customStyles from '@/plugins/custom-styles';
import { EventBus } from '@/plugins/event-bus';
import GoogleTagManagerLoader from '@/plugins/google_tag_manager_loader';
import handleNativeClickEvent from '@/shared/utils/analytics/handleNativeClickEvent';
import { handleProtectedRoute, handlePublicRoute } from '@/parent/services/request-routing';
import { hideInaccessibleRecaptchaElements } from '@/plugins/a11y';
import i18n from '@/plugins/i18n';
import { registerStandardComponents } from '@/standard';
import vuetify from '@/plugins/vuetify';

import App from '../src/parent/App.vue';
import router from '../src/parent/router';
import store from '../src/parent/store';

router.beforeEach((to) => {
  if (to.path === '/sign_out') return true;

  if (to.meta.public) {
    return handlePublicRoute({ store, to });
  }

  const { config, profile } = store.state;
  const needsPassword =
    config.enable_password_authentication &&
    (to.query.password_reset === 'true' || profile?.password_reset_required === true);
  if (needsPassword && to.name !== 'RegisterPassword') {
    return { name: 'RegisterPassword', query: { password_reset: to.query.password_reset } };
  }

  return handleProtectedRoute({ store, to });
});

Api.publicApi.site.configuration.get((resp) => {
  const config = resp.data;

  window.site_id = config.site_id;

  const app = createApp(App);

  app.config.globalProperties.$role = 'parent';
  app.config.globalProperties.$a = assets;
  app.provide('assets', assets);
  app.config.globalProperties.$eventBus = EventBus;
  app.provide('eventBus', EventBus);
  app.config.globalProperties.window = window;

  app.use(i18n);
  app.use(router);
  app.use(store);
  app.use(vuetify);

  setConfig();

  registerStandardComponents(app);
  app.mount('#app');

  resetVuetifyTheme();

  customStyles.generate(store.state.brand.colors);
  const faviconUrl = config.favicon_url;
  if (faviconUrl) document.getElementById('favicon').setAttribute('href', faviconUrl);

  document.addEventListener('click', (event) => {
    store.commit('resetTimeout');

    handleNativeClickEvent(event);
  });

  if (store.state.site.google_tag_manager_id) {
    GoogleTagManagerLoader.activateGtm(
      window,
      document,
      'script',
      'dataLayer',
      store.state.site.google_tag_manager_id,
    );
  }

  // Add bf-search scripts at the end of body
  if (window.VUE_APP_BF_SEARCH_MODULE_URL) {
    const scriptModule = document.createElement('script');
    scriptModule.setAttribute('type', 'module');
    scriptModule.setAttribute('src', window.VUE_APP_BF_SEARCH_MODULE_URL);
    scriptModule.setAttribute('data-stencil', '');
    document.body.appendChild(scriptModule);
  }

  if (window.VUE_APP_BF_SEARCH_NOMODULE_URL) {
    const scriptNoModule = document.createElement('script');
    scriptNoModule.setAttribute('nomodule', '');
    scriptNoModule.setAttribute('src', window.VUE_APP_BF_SEARCH_NOMODULE_URL);
    scriptNoModule.setAttribute('data-stencil', '');
    document.body.appendChild(scriptNoModule);
  }

  store.dispatch('page');

  hideInaccessibleRecaptchaElements();

  function setConfig() {
    store.commit('setBrand', config.brand);
    store.commit('setConfig', config);
    store.commit('setFeatures', config.features);
    store.commit('setPages', config.pages);
    store.commit('setLanguages', config.languages);
    store.commit('setSchemas', config.schemas);
    store.commit('setSite', config.site);
    store.commit('setAgeGroups', config.age_groups);
    store.commit('setSiteWebpageId', config.site_webpage_id);
    store.commit('setUserSessionTimeout', config.user_session_timeout_minutes);
  }

  function resetVuetifyTheme() {
    vuetify.theme.themes.value.light.colors.primary = store.state.brand.colors.primary;
    vuetify.theme.themes.value.light.colors.secondary = store.state.brand.colors.secondary;
    vuetify.theme.themes.value.light.colors.accent = store.state.brand.colors.accent;
  }
});
